// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AssetCreationImportDialog } from '@/asset/components/asset-creation-import-dialog';
import { AssetDataImportDialog } from '@/asset/components/asset-data-import-dialog';
import { AssetMetadataImportDialog } from '@/asset/components/asset-metadata-import-dialog';
import { CreateAssetDialog } from '@/asset/components/create-asset-dialog';
import {
  AddRounded,
  Close,
  SettingsOutlined,
  SyncOutlined,
  UploadRounded,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  styled,
} from '@mui/material';

const OptionCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
  marginBottom: theme.spacing(2),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 50,
  height: 50,
  backgroundColor: theme.palette.primary.main,
  borderRadius: '50%',
  padding: '2px',
  flexShrink: 0,
}));

const InnerIconContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  backgroundColor: 'white',
  borderRadius: '50%',
}));

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export function AssetImportModal(props: Props) {
  const createAssetDialog = useDisclosure();
  const uploadAssetCreationDialog = useDisclosure();
  const uploadAssetDataDialog = useDisclosure();
  const uploadAssetMetadataDialog = useDisclosure();
  /**
   * Handles opening create asset dialog and closing main modal
   */
  const handleCreateAssetClick = () => {
    createAssetDialog.open();
    props.onClose();
  };

  /**
   * Handles opening upload asset creation dialog and closing main modal
   */
  const handleUploadAssetCreationClick = () => {
    uploadAssetCreationDialog.open();
    props.onClose();
  };

  /**
   * Handles opening upload asset data dialog and closing main modal
   */
  const handleUploadAssetDataClick = () => {
    uploadAssetDataDialog.open();
    props.onClose();
  };

  /**
   * Handles opening upload asset metadata dialog and closing main modal
   */
  const handleUploadAssetMetadataClick = () => {
    uploadAssetMetadataDialog.open();
    props.onClose();
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="md"
        open={props.isOpen}
        onClose={props.onClose}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Add asset data
          <IconButton onClick={props.onClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <OptionCard onClick={handleCreateAssetClick}>
              <CardContent
                sx={{ display: 'flex', alignItems: 'center', p: 3, gap: 3 }}
              >
                <IconContainer>
                  <InnerIconContainer>
                    <AddRounded color="primary" fontSize="large" />
                  </InnerIconContainer>
                </IconContainer>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Add individual asset
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create a single asset by entering its details manually.
                    Perfect for adding one-off assets to your inventory.
                  </Typography>
                </Box>
              </CardContent>
            </OptionCard>

            <OptionCard onClick={handleUploadAssetCreationClick}>
              <CardContent
                sx={{ display: 'flex', alignItems: 'center', p: 3, gap: 3 }}
              >
                <IconContainer>
                  <InnerIconContainer>
                    <UploadRounded color="primary" fontSize="large" />
                  </InnerIconContainer>
                </IconContainer>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Import new assets
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upload a list of new assets to be created in your inventory.
                    Perfect for bulk importing assets from external systems.
                  </Typography>
                </Box>
              </CardContent>
            </OptionCard>

            <OptionCard onClick={handleUploadAssetDataClick}>
              <CardContent
                sx={{ display: 'flex', alignItems: 'center', p: 3, gap: 3 }}
              >
                <IconContainer>
                  <InnerIconContainer>
                    <SyncOutlined color="primary" fontSize="large" />
                  </InnerIconContainer>
                </IconContainer>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Update existing assets
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Update basic info for existing assets. This includes
                    descriptions, statuses, inventory dates, and initial
                    locations.
                  </Typography>
                </Box>
              </CardContent>
            </OptionCard>

            <OptionCard onClick={handleUploadAssetMetadataClick}>
              <CardContent
                sx={{ display: 'flex', alignItems: 'center', p: 3, gap: 3 }}
              >
                <IconContainer>
                  <InnerIconContainer>
                    <SettingsOutlined color="primary" fontSize="large" />
                  </InnerIconContainer>
                </IconContainer>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Update asset metadata
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Update custom fields that you've defined for your products.
                    These are specific data points you've added for your
                    organization.
                  </Typography>
                </Box>
              </CardContent>
            </OptionCard>
          </Box>
        </DialogContent>
      </Dialog>

      <CreateAssetDialog
        isOpen={createAssetDialog.isOpen}
        onClose={createAssetDialog.close}
      />
      <AssetCreationImportDialog
        isOpen={uploadAssetCreationDialog.isOpen}
        onClose={uploadAssetCreationDialog.close}
      />
      <AssetDataImportDialog
        isOpen={uploadAssetDataDialog.isOpen}
        onClose={uploadAssetDataDialog.close}
      />
      <AssetMetadataImportDialog
        isOpen={uploadAssetMetadataDialog.isOpen}
        onClose={uploadAssetMetadataDialog.close}
      />
    </>
  );
}
